// =====================================================================
// V4C Predictive Search 切替 (per-product fetch + 即時 loading 方式)
// =====================================================================
//
// 役割:
//   PredictiveSearchComponent (assets/predictive-search.js) が sectionRenderer
//   経由で叩く `/search/suggest?q=...&section_id=predictive-search` をフックして、
//   V4C の検索結果に差し替える。
//
// フロー (Phase 2 / 1b):
//   1. window.fetch を wrap して predictive search 呼び出しを検出
//   2. *即座に* loading spinner 入りの shell HTML を Response で返す
//      (PredictiveSearchComponent が morph で挿入 → 1 frame で dropdown が
//       開きスピナーが回る。体感初期表示 200ms 以下)
//   3. 同時に background で V4cClient.search + V4cCardFetch.fetchInOrder を実行
//   4. 完了したら #predictive-search-results の innerHTML を実カードリストに置換
//   5. 連続入力で stale な結果が来た場合は generation id で破棄
//   6. V4C zero-hit / 失敗時は no-results inner に置換
//
// Feature flag:
//   - 本 snippet (snippets/v4c-predictive-search.liquid) 自体が theme setting
//     `settings.enable_v4c_predictive` でガードされている。merchant が OFF にすると
//     script tag ごと render されないので、JS 側で別途 isV4cOn() を見る必要は無い
//     (旧実装は localStorage `v4c_enabled === '1'` を必須にしていたが、live 全 user
//     展開には merchant setting で十分なので gate を撤去)。
//
// 互換:
//   - predictive-search-fallback.js が同じ window.fetch を別途 wrap している。
//     scripts.liquid 上で fallback の "後" に v4c-predictive-search が render される
//     → v4c が outer wrap、fallback が inner。fetch コールはまず v4c が intercept、
//       hijack 条件外なら inner (= fallback) に委譲
//   - V4C zero-hit / 失敗時は **no-results 表示** に固定 (Phase 1 の native fallback
//     経路はこの方式では使わない。skeleton を返した時点で fetch は終了しているため)
// =====================================================================

(function () {
  if (typeof window.fetch !== 'function') return;
  if (window.__v4cPredictiveInstalled) return;
  window.__v4cPredictiveInstalled = true;

  // 旧 localStorage `v4c_enabled === '1'` gate は撤去済。
  // 本 script は snippets/v4c-predictive-search.liquid 内で
  // `{%- if settings.enable_v4c_predictive -%}` でガードされており、theme editor
  // で OFF にすれば snippet ごと出力されない。

  var ORIG_FETCH = window.fetch.bind(window);

  // 連続入力の stale な background 結果を破棄するための世代カウンタ
  var currentGeneration = 0;

  function isPredictiveCall(url) {
    if (url.searchParams.get('section_id') !== 'predictive-search') return false;
    return url.pathname.endsWith('/search') || url.pathname.endsWith('/search/suggest');
  }

  // Horizon の RecentlyViewed が叩く `q=id:1234 OR id:5678 ...` 形式の
  // Storefront search syntax は V4C で解釈不能 (空ヒット → no-results 表示)
  // になるため hijack せず native predictive にフォールスルーする。
  // この query は modal 開いた瞬間など free-text 検索とは別経路で発動するので、
  // ユーザーが直後に入力した検索結果と race して no-results が残る原因になる。
  function isShopifyIdLookup(q) {
    return /^\s*id:\d+(\s+OR\s+id:\d+)*\s*$/i.test(q);
  }

  // CSS は head に一度だけ inject。Phase 2 / 1b で導入したため V4C 利用者にだけ
  // ロードされる。theme 全体の CSS バンドルには入れない (theme conflict 回避)。
  function ensureLoadingStyle() {
    if (document.getElementById('v4c-loading-style')) return;
    var style = document.createElement('style');
    style.id = 'v4c-loading-style';
    style.textContent =
      '.v4c-loading-state{display:flex;align-items:center;justify-content:center;min-height:200px;padding:32px 16px;}' +
      '.v4c-spinner{width:28px;height:28px;border:2px solid rgb(var(--color-foreground-rgb) / 0.15);' +
        'border-top-color:rgb(var(--color-foreground-rgb) / 0.7);border-radius:50%;' +
        'animation:v4c-spin 0.7s linear infinite;}' +
      '@keyframes v4c-spin{to{transform:rotate(360deg)}}';
    document.head.appendChild(style);
  }

  // PredictiveSearchComponent の morph target と互換な dropdown 外殻。
  //   - root = <div id="predictive-search-results"> (predictive-search.js が ref で参照)
  //   - 初回 morph 後は slot 単位で innerHTML を置換していく
  //   - data-v4c-suggestions-slot = Pattern A 統合 list (V4C complete の結果)
  //   - data-v4c-products-slot    = 商品 grid (V4C search の結果)
  function buildShellRoot(productsInnerHtml) {
    return (
      '<div id="predictive-search-results" class="predictive-search-dropdown" role="listbox" data-transition-container ' +
        'style="--color-shadow: rgb(var(--color-foreground-rgb) / var(--opacity-10-25));">' +
        '<div data-v4c-suggestions-slot></div>' +
        '<div data-v4c-products-slot>' +
          productsInnerHtml +
        '</div>' +
      '</div>'
    );
  }

  function escapeHtml(s) {
    if (typeof s !== 'string') return '';
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // 各 locale 用ラベル。v4c-search-suggestion.js と同じ構造。
  function getLabels() {
    var lang = (window.V4cClient && typeof window.V4cClient.getLocale === 'function')
      ? window.V4cClient.getLocale()
      : 'ja';
    switch (lang) {
      case 'en':    return { brand: 'Brand',  category: 'Category', query: 'Search',   viewAll: 'View all search results',   noResults: 'No matching products found.', loading: 'Searching' };
      case 'es':    return { brand: 'Marca',  category: 'Categoría', query: 'Búsqueda', viewAll: 'Ver todos los resultados',  noResults: 'No se encontraron productos.', loading: 'Buscando' };
      case 'zh-CN': return { brand: '品牌',   category: '类别',      query: '搜索',    viewAll: '查看所有搜索结果',           noResults: '未找到匹配的商品。',           loading: '搜索中' };
      case 'zh-TW': return { brand: '品牌',   category: '類別',      query: '搜尋',    viewAll: '查看所有搜尋結果',           noResults: '找不到符合的商品。',           loading: '搜尋中' };
      default:      return { brand: 'ブランド', category: 'カテゴリ',  query: '候補',    viewAll: '検索結果をすべて表示',       noResults: '該当商品が見つかりませんでした。', loading: '検索中' };
    }
  }

  // brand / category は targetHandle → canonical collection title (current locale 翻訳済)
  // を window.V4cCollectionTitles で逆引きして表示。alias 入力 (例: 「グッチ」「gucci」) で
  // ヒットしても suggestion 表示は「GUCCI」など Shopify collection 上の正式名に統一。
  // window.V4cCollectionTitles は snippets/v4c-collection-titles.liquid が注入する
  // (snippets/v4c-predictive-search.liquid から render 済)。
  function resolveDisplayText(c) {
    var map = window.V4cCollectionTitles;
    if (map && c.targetHandle && typeof map[c.targetHandle] === 'string') {
      return map[c.targetHandle];
    }
    return c.text || '';
  }

  // Pattern A 統合 list の HTML を組み立てる。
  //   - brand / category 行はラベル「ブランド」/「カテゴリ」付きで /collections/<handle> へ
  //   - query 行はラベル「候補」付きで /search?q=<text> へ
  //   - PredictiveSearchComponent (predictive-search.js) のキーボードナビが
  //     ref="resultsItems[]" の要素を巡回するため、各 li に明示的に付与
  function buildSuggestionsHtml(completions) {
    if (!completions || completions.length === 0) return '';
    var labels = getLabels();
    var lis = [];
    for (var i = 0; i < completions.length; i++) {
      var c = completions[i] || {};
      var type = c.type || 'query';
      var targetHandle = c.targetHandle || '';
      // brand / category は canonical title、query は入力したクエリ自体を尊重
      var text =
        (type === 'brand' || type === 'category')
          ? resolveDisplayText(c)
          : (c.text || '');
      if (!text) continue;

      var label, href;
      if (type === 'brand' || type === 'category') {
        label = type === 'brand' ? labels.brand : labels.category;
        href = targetHandle
          ? '/collections/' + encodeURIComponent(targetHandle)
          : '/search?q=' + encodeURIComponent(text);
      } else {
        label = labels.query;
        href = '/search?q=' + encodeURIComponent(text);
      }

      lis.push(
        '<li class="v4c-suggestion-row v4c-suggestion-row--' + type + '" ' +
            'ref="resultsItems[]" role="option">' +
          '<a href="' + href + '" class="v4c-suggestion-row__link">' +
            '<span class="v4c-suggestion-row__text">' + escapeHtml(text) + '</span>' +
            '<span class="v4c-suggestion-row__label">' + escapeHtml(label) + '</span>' +
          '</a>' +
        '</li>'
      );
    }
    if (lis.length === 0) return '';
    // class に `predictive-search-results__list` を含めることで、Horizon の
    // PredictiveSearchComponent (predictive-search.js) の #allResultsItems
    // getter が ul を「container」として認識し、内部 li (ref="resultsItems[]")
    // を巡回してくれる → ↑↓Tab/Enter のキーボードナビが効く
    return '<ul class="v4c-suggestion-list predictive-search-results__list" role="listbox">' + lis.join('') + '</ul>';
  }

  function buildLoadingInner() {
    var labels = getLabels();
    return (
      '<div class="predictive-search-results__inner v4c-loading-state" data-search-results>' +
        '<div class="v4c-spinner" role="progressbar" aria-label="' + escapeHtml(labels.loading) + '"></div>' +
      '</div>'
    );
  }

  function buildNoResultsInner() {
    var labels = getLabels();
    return (
      '<div class="predictive-search-results__inner" data-search-results>' +
        '<p class="predictive-search-results__no-results">' + escapeHtml(labels.noResults) + '</p>' +
      '</div>'
    );
  }

  // 必須要件:
  //  - 各 card に class="predictive-search-results__card predictive-search-results__card--product"
  //    と ref="resultsItems[]" の両方を付ける (キーボードナビゲーション + #allResultsItems の selector)
  //  - 親 ul に class "predictive-search-results__wrapper-products" を含める (Horizon CSS 用)
  //  - 商品リスト直下に "検索結果をすべて表示" リンクを inline で出す (Phase 2 で fixed footer から移行)
  function buildResultsInner(cards, query) {
    var liItems = '';
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (!card) continue;
      liItems +=
        '<li class="predictive-search-results__card predictive-search-results__card--product" ref="resultsItems[]">' +
        card.outerHTML +
        '</li>';
    }
    if (liItems === '') return buildNoResultsInner();
    var labels = getLabels();
    var viewAllHref = '/search?q=' + encodeURIComponent(query || '');
    // 元の下部固定ボタン (.button .predictive-search__search-button) と同じクラスを
    // 付けることで Horizon theme の primary button 配色 + hover アニメを継承する。
    var viewAllHtml =
      '<div class="v4c-view-all-wrapper">' +
        '<a href="' + viewAllHref + '" ' +
            'class="button predictive-search__search-button v4c-view-all-link" ' +
            'data-v4c-view-all="true">' +
          escapeHtml(labels.viewAll) +
        '</a>' +
      '</div>';
    return (
      '<div class="predictive-search-results__inner" data-search-results>' +
        '<div id="predictive-search-products" class="predictive-search-results__products">' +
          '<ul class="predictive-search-results__list predictive-search-results__wrapper-products list-unstyled">' +
            liItems +
          '</ul>' +
          viewAllHtml +
        '</div>' +
      '</div>'
    );
  }

  // root を Light DOM / Shadow DOM / Component ref から探す。
  //  - Phase 1 当初は document.getElementById で取れたが、cache hit で
  //    background work (V4C + per-product fetch) が PredictiveSearchComponent の
  //    morph より早く完走するケースで「root がまだ DOM に無い」状態が発生した。
  //  - Component (predictive-search-component) の `refs.predictiveSearchResults`
  //    経由なら shadow DOM/template 配下でも参照可能。
  function findPsrRoot() {
    var byDoc = document.getElementById('predictive-search-results');
    if (byDoc) return byDoc;
    var comps = document.querySelectorAll('predictive-search-component');
    for (var i = 0; i < comps.length; i++) {
      var c = comps[i];
      if (c && c.refs && c.refs.predictiveSearchResults) {
        return c.refs.predictiveSearchResults;
      }
    }
    return null;
  }

  // root が見つからなければ短い間隔で retry。cache hit 時の race を吸収する。
  // 最大 1 秒 (10 回 × 100ms) で諦める。
  function injectProducts(innerHtml, attemptsLeft) {
    if (typeof attemptsLeft !== 'number') attemptsLeft = 10;
    var psr = findPsrRoot();
    if (psr) {
      var slot = psr.querySelector('[data-v4c-products-slot]');
      if (slot) {
        slot.innerHTML = innerHtml;
      } else {
        // slot が見つからない (例: 古い shell が描画されている) ときは root 全体を置換
        psr.innerHTML = innerHtml;
      }
      return;
    }
    if (attemptsLeft <= 0) {
      console.warn('[v4c-predictive] psr root never appeared, giving up');
      return;
    }
    setTimeout(function () {
      injectProducts(innerHtml, attemptsLeft - 1);
    }, 100);
  }

  // suggestions slot に Pattern A 統合 list を inject。空配列なら slot を空にする。
  function injectSuggestions(completions, attemptsLeft) {
    if (typeof attemptsLeft !== 'number') attemptsLeft = 10;
    var psr = findPsrRoot();
    if (psr) {
      var slot = psr.querySelector('[data-v4c-suggestions-slot]');
      if (!slot) return;
      slot.innerHTML = buildSuggestionsHtml(completions);
      return;
    }
    if (attemptsLeft <= 0) return;
    setTimeout(function () {
      injectSuggestions(completions, attemptsLeft - 1);
    }, 100);
  }

  window.fetch = async function (input, init) {
    var url;
    try {
      var raw = typeof input === 'string' ? input : (input && input.url);
      if (!raw) return ORIG_FETCH(input, init);
      url = new URL(raw, location.origin);
    } catch (_) {
      return ORIG_FETCH(input, init);
    }

    if (!isPredictiveCall(url)) return ORIG_FETCH(input, init);

    var q = url.searchParams.get('q');
    if (!q || !q.trim()) return ORIG_FETCH(input, init);

    // V4C client / card-fetch helper が未 load なら native にフォールスルー
    if (!window.V4cClient || !window.V4cCardFetch) return ORIG_FETCH(input, init);

    // Recently viewed 等が叩く id: 形式は V4C 対象外
    if (isShopifyIdLookup(q)) return ORIG_FETCH(input, init);

    var query = q.trim();
    var myGen = ++currentGeneration;

    // V4C complete (Pattern A 統合 list) を並行 fetch。商品 search とは独立した
    // background 処理。失敗時は無音 fail (商品 grid 側に影響しない)。
    if (typeof window.V4cClient.complete === 'function') {
      (async function () {
        try {
          var resp = await window.V4cClient.complete({ query: query, limit: 8 });
          if (myGen !== currentGeneration) return;
          injectSuggestions(resp.completions);
        } catch (e) {
          console.warn('[v4c-predictive] complete failed', e);
        }
      })();
    }

    // background で V4C + section render を実行 (proxy 内で完結)。完了したら
    // inner を置換。
    //
    // Phase 3 / server-side hydration: includeSection を指定すると proxy 内で
    // V4C 検索 + handle hydrate + Shopify section render API 呼び出しを連続
    // 実行し、完成 HTML を sectionHtml で返す。ブラウザは 1 fetch で完結
    // する → App Proxy edge オーバーヘッド + 2 段目 client→Shopify CDN fetch
    // が削減される。
    (async function () {
      try {
        var v4c = await window.V4cClient.search({
          query: query,
          limit: 6,
          includeSection: 'v4c-search-batch',
        });
        if (myGen !== currentGeneration) return; // stale: 後続入力で世代が進んだ

        if (!v4c.results || v4c.results.length === 0) {
          injectProducts(buildNoResultsInner());
          return;
        }

        var productIds = v4c.results
          .map(function (r) { return r.productId; })
          .filter(Boolean);
        if (productIds.length === 0) {
          injectProducts(buildNoResultsInner());
          return;
        }

        // server-side hydration が成功していれば sectionHtml を使う。失敗時は
        // ブラウザから section render API を叩く client-side fallback に倒す。
        var batchHtml = v4c.sectionHtml;
        if (!batchHtml) {
          var idQuery = productIds.map(function (id) { return 'id:' + id; }).join(' OR ');
          var batchUrl =
            '/search?q=' + encodeURIComponent(idQuery) +
            '&sections=v4c-search-batch';
          var batchRes = await ORIG_FETCH(batchUrl, {
            headers: { Accept: 'application/json' },
          });
          if (myGen !== currentGeneration) return;
          if (!batchRes.ok) throw new Error('search-batch status ' + batchRes.status);
          var batchJson = await batchRes.json();
          batchHtml = batchJson['v4c-search-batch'] || '';
        }

        var tpl = document.createElement('template');
        tpl.innerHTML = batchHtml;
        var cardsByPid = new Map();
        tpl.content.querySelectorAll('product-card').forEach(function (c) {
          var pid = c.dataset.productId;
          if (pid) cardsByPid.set(pid, c);
        });

        // V4C ranking 順に並べる (Shopify 検索の relevance 順は V4C と異なる)
        var orderedCards = productIds.map(function (pid) {
          return cardsByPid.get(pid) || null;
        });

        injectProducts(buildResultsInner(orderedCards, query));
      } catch (e) {
        console.warn('[v4c-predictive] background fetch failed', e);
        if (myGen !== currentGeneration) return;
        injectProducts(buildNoResultsInner());
      }
    })();

    ensureLoadingStyle();
    return new Response(buildShellRoot(buildLoadingInner()), {
      status: 200,
      headers: { 'Content-Type': 'text/html; charset=utf-8' },
    });
  };
})();
